import { useState, useEffect, useCallback } from 'react';
import {
  listThumbnails,
  createThumbnail,
  selectThumbnail,
  deleteThumbnail,
  generateImages,
  persistMedia,
  downloadMedia,
  type ContentProject,
  type ContentThumbnail,
} from '@/lib/contentStudio';

interface Props {
  project: ContentProject;
}

/**
 * Miniatures (spec 39) : l'IA génère plusieurs propositions de miniature à
 * partir du titre / hook / style du projet. L'utilisateur choisit celle qui
 * devient la vignette du projet, ou la télécharge.
 */
export default function ThumbnailsPanel({ project }: Props) {
  const [items, setItems] = useState<ContentThumbnail[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const aspect = project.aspect_ratio === '9:16' ? '9:16' : project.aspect_ratio === '1:1' ? '1:1' : '16:9';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await listThumbnails(project.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  const buildPrompt = () => {
    const hook = project.visual_context || '';
    return `Miniature (thumbnail) très accrocheuse pour la vidéo « ${project.name} ». ${hook ? `Accroche : ${hook}. ` : ''}Style ${project.style || 'cinématographique documentaire'}, contraste élevé, composition forte, sujet net et marquant, couleurs vives mais harmonieuses, lisible en petit format. Illustration principale mise en avant, pas de surcharge.`;
  };

  const handleGenerate = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const prompt = buildPrompt();
      const urls = await generateImages({ prompt, n: 3, aspectRatio: aspect, resolution: '1k' });
      if (urls.length === 0) throw new Error("L'IA n'a renvoyé aucune miniature.");
      const created: ContentThumbnail[] = [];
      for (const rawUrl of urls) {
        // Re-hébergement pour un lien permanent.
        let url = rawUrl;
        // eslint-disable-next-line no-await-in-loop
        try { url = await persistMedia(rawUrl, `miniature-${Date.now()}.jpg`, 'content-studio/thumbnails', 'image/jpeg'); } catch { url = rawUrl; }
        // eslint-disable-next-line no-await-in-loop
        const row = await createThumbnail(project.id, { prompt, url, provider: 'xAI Grok Imagine' });
        if (row) created.push(row);
      }
      setItems((prev) => [...created, ...prev]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Échec de la génération de miniatures');
    } finally {
      setBusy(false);
    }
  };

  const handleSelect = async (t: ContentThumbnail) => {
    if (!t.url) return;
    try {
      await selectThumbnail(project.id, t.id, t.url);
      setItems((prev) => prev.map((x) => ({ ...x, selected: x.id === t.id })));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteThumbnail(id);
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <h3 className="text-base font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className="ri-image-2-line text-accent-500"></i>Miniatures
          </h3>
          <p className="text-xs text-foreground-500 mt-1 max-w-2xl">
            Générez plusieurs propositions de miniature et choisissez celle qui devient la vignette de votre projet.
          </p>
        </div>
        <button
          type="button"
          onClick={handleGenerate}
          disabled={busy}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
        >
          {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-sparkling-2-line"></i>}
          {busy ? 'Génération…' : 'Générer des miniatures'}
        </button>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
          <i className="ri-error-warning-line"></i>{error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <i className="ri-loader-4-line animate-spin text-xl text-accent-500"></i>
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-14 bg-background-50 border border-background-200/70 rounded-xl text-center">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-image-2-line text-xl text-foreground-400"></i>
          </div>
          <p className="text-sm font-medium text-foreground-700">Aucune miniature</p>
          <p className="text-xs text-foreground-500 mt-1 max-w-xs">Générez des propositions à partir de votre titre et de votre style.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {items.map((t) => (
            <div key={t.id} className={`bg-background-50 border rounded-xl overflow-hidden flex flex-col ${t.selected ? 'border-accent-400' : 'border-background-200/70'}`}>
              <div className="relative aspect-video bg-background-100 overflow-hidden">
                {t.url ? <img src={t.url} alt="Miniature" className="w-full h-full object-cover" /> : <i className="ri-image-line text-2xl text-foreground-300"></i>}
                {t.selected && (
                  <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-accent-500 text-background-50 text-[10px] font-semibold">
                    <i className="ri-check-line"></i>Vignette du projet
                  </span>
                )}
              </div>
              <div className="p-2.5 flex items-center gap-1.5">
                {t.url && !t.selected && (
                  <button
                    type="button"
                    onClick={() => handleSelect(t)}
                    className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-md bg-primary-500 text-background-50 text-[11px] font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-check-line"></i>Choisir
                  </button>
                )}
                {t.url && (
                  <button
                    type="button"
                    onClick={() => downloadMedia(t.url as string, `miniature-${t.id}.jpg`)}
                    className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-md border border-background-200/70 text-[11px] font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-download-2-line"></i>Télécharger
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleDelete(t.id)}
                  className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                  title="Supprimer"
                >
                  <i className="ri-delete-bin-line text-sm"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}